import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Badge,
    Box,
    Button,
    Column,
    Columns,
    Dropdown,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { VendorResponse } from "../../services/vendorService";

interface VendorListTableProps {
    vendorList?: Array<VendorResponse> | null;
    onCreateVendor?: () => void;
    onEditVendor?: (vendor: VendorResponse) => void;
}

const VendorListTable: React.FC<VendorListTableProps> = ({
    vendorList,
    onCreateVendor,
    onEditVendor,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState<string>("all");

    const filteredVendors = useMemo(() => {
        if (!vendorList) {
            return vendorList;
        }

        return vendorList.filter((vendor) => {
            const normalizedSearch = searchTerm.trim().toLowerCase();
            const normalizedStatus = Boolean(vendor.ven_active) ? "active" : "inactive";

            const matchesSearch =
                !normalizedSearch ||
                vendor.ven_code?.toLowerCase().includes(normalizedSearch) ||
                vendor.ven_Name?.toLowerCase().includes(normalizedSearch) ||
                vendor.ven_titile?.toLowerCase().includes(normalizedSearch) ||
                vendor.ven_email?.toLowerCase().includes(normalizedSearch) ||
                vendor.ven_tep1?.toLowerCase().includes(normalizedSearch);

            const matchesStatus = selectedStatus === "all" || normalizedStatus === selectedStatus;

            return matchesSearch && matchesStatus;
        });
    }, [vendorList, searchTerm, selectedStatus]);

    const showActions = typeof onEditVendor === "function";

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        {onCreateVendor ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateVendor}
                                >
                                    {t("page.vendor_management.list.button.create_new", {
                                        defaultValue: "Create New Vendor",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center" gap="medium">
                        <TextField
                            placeholder={t("page.vendor_management.list.search.placeholder", {
                                defaultValue: "Search Vendors...",
                            })}
                            aria-label={t("page.vendor_management.list.search.aria", {
                                defaultValue: "Search Vendors",
                            })}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            value={searchTerm}
                        />
                        <Dropdown
                            id="vendor-status-filter"
                            aria-label={t("page.vendor_management.list.status_filter.aria", {
                                defaultValue: "Status Filter",
                            })}
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.currentTarget.value)}
                        >
                            <option value="all">
                                {t("page.vendor_management.list.status_filter.all", {
                                    defaultValue: "All Status",
                                })}
                            </option>
                            <option value="active">
                                {t("page.vendor_management.list.status_filter.active", {
                                    defaultValue: "Active",
                                })}
                            </option>
                            <option value="inactive">
                                {t("page.vendor_management.list.status_filter.inactive", {
                                    defaultValue: "Inactive",
                                })}
                            </option>
                        </Dropdown>
                    </Box>
                </Column>
            </Columns>

            <Table
                label={t("page.vendor_management.list.table.label", {
                    defaultValue: "Vendors List Table",
                })}
            >
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.code", { defaultValue: "Code" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.accno", { defaultValue: "Account No" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.name", { defaultValue: "Vendor Name" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.title", { defaultValue: "Title" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.group", { defaultValue: "Group" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.email", { defaultValue: "Email" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.vendor_management.list.table.headers.phone", { defaultValue: "Phone" })}</Text></TableHeaderCell>
                        <TableHeaderCell align="right"><Text>{t("page.vendor_management.list.table.headers.balance", { defaultValue: "Balance" })}</Text></TableHeaderCell>
                        <TableHeaderCell align="center"><Text>{t("page.vendor_management.list.table.headers.status", { defaultValue: "Status" })}</Text></TableHeaderCell>
                        {showActions ? <TableHeaderCell align="right"><Text>{t("page.vendor_management.list.table.headers.actions", { defaultValue: "Actions" })}</Text></TableHeaderCell> : null}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {filteredVendors && filteredVendors.length > 0 ? (
                        filteredVendors.map((vendor) => {
                            const isActive = Boolean(vendor.ven_active);
                            return (
                                    <TableRow key={vendor.ven_id ?? vendor.ven_code ?? "vendor-row"}>
                                        <TableCell><Text>{vendor.ven_code || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                        <TableCell><Text>{vendor.ven_accno || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                        <TableCell><Text>{vendor.ven_Name || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                    <TableCell><Text>{vendor.ven_titile || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                    <TableCell><Text>{vendor.ven_groupid || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                    <TableCell><Text>{vendor.ven_email || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                    <TableCell><Text>{vendor.ven_tep1 || t("page.vendor_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                    <TableCell align="right"><Text>{vendor.ven_balance ?? "0.00"}</Text></TableCell>
                                    <TableCell align="center">
                                        <Badge tone={isActive ? "positive" : "caution"}>
                                            {isActive
                                                ? t("page.vendor_management.list.badge.active", { defaultValue: "Active" })
                                                : t("page.vendor_management.list.badge.inactive", { defaultValue: "Inactive" })}
                                        </Badge>
                                    </TableCell>
                                    {showActions ? (
                                        <TableCell align="right">
                                            <Inline space="small">
                                                <TooltipRenderer tooltip={<Text>{t("page.vendor_management.list.tooltip.edit", { defaultValue: "Edit Vendor" })}</Text>}>
                                                    {({ triggerProps }) => (
                                                        <Box
                                                            onClick={() => onEditVendor && onEditVendor(vendor)}
                                                            cursor="pointer"
                                                            {...triggerProps}
                                                        >
                                                            <IconEdit tone="info" />
                                                        </Box>
                                                    )}
                                                </TooltipRenderer>
                                            </Inline>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colspan={showActions ? 10 : 9} align="center">
                                <Text>{t("page.vendor_management.list.empty", { defaultValue: "No vendors found." })}</Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default VendorListTable;
